import { useState } from 'react'
import { useDroppable } from '@dnd-kit/core'
import type { PipelineCard as PipelineCardType, ProjectStage } from '@1130-studio/shared'
import { PipelineCard } from './PipelineCard'
import { cn } from '../../lib/utils'

interface ArchiveSectionProps {
  stage: ProjectStage
  cards: PipelineCardType[]
  onCardClick?: (card: PipelineCardType) => void
}

export function ArchiveSection({ stage, cards, onCardClick }: ArchiveSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const { setNodeRef, isOver } = useDroppable({
    id: stage.id,
  })

  return (
    <div className="mt-6 border-t border-white/10 pt-6">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-red-500/5 hover:bg-red-500/10 border border-red-500/10 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <span className="text-sm font-semibold text-red-400">
            {stage.name}
          </span>
          <span className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center text-xs font-semibold text-red-400">
            {cards.length}
          </span>
        </div>
        <svg
          className={cn(
            'w-4 h-4 text-red-400 transition-transform',
            isExpanded && 'rotate-180'
          )}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {/* Drop Zone (always visible for dropping) */}
      <div
        ref={setNodeRef}
        className={cn(
          'mt-3 min-h-[60px] rounded-xl border-2 border-dashed transition-all',
          isOver
            ? 'border-red-500/50 bg-red-500/10'
            : 'border-red-500/20 bg-red-500/5'
        )}
      >
        {!isExpanded && cards.length === 0 && (
          <div className="flex items-center justify-center h-[60px] text-sm text-red-400/50">
            Перетащите сюда для архивации
          </div>
        )}

        {/* Expanded Content */}
        {isExpanded && (
          <div className="p-4">
            {cards.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {cards.map((card) => (
                  <PipelineCard
                    key={card.id}
                    card={card}
                    onClick={() => onCardClick?.(card)}
                  />
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center py-8 text-sm text-red-400/50">
                Архив пуст
              </div>
            )}
          </div>
        )}

        {/* Collapsed with cards - show hint */}
        {!isExpanded && cards.length > 0 && (
          <div className="flex items-center justify-center h-[60px] text-sm text-red-400/50">
            {cards.length} {cards.length === 1 ? 'карточка' : cards.length < 5 ? 'карточки' : 'карточек'} в архиве
          </div>
        )}
      </div>
    </div>
  )
}
